import { DesignElement, isDesignElementRisk } from "@design-controls/types";
import { TFunction } from "i18next";
import {
  COMPARE_SECTIONS,
  CompareSectionId,
  linkedElementsFor,
  propertiesOf,
} from "./compareSections";

export type CompareHtml = { older: string; viewed: string };

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const paragraphs = (rows: string[]): string =>
  rows.map((row) => `<p>${row}</p>`).join("");

const formatVersion = (major?: number | null, minor?: number | null): string =>
  major == null ? "" : ` v${major}.${minor ?? 0}`;

// Rich text left empty by the editor is still markup, e.g. "<p>&nbsp;</p>".
const isBlankHtml = (html: string): boolean =>
  !/<img\b/i.test(html) && !html.replace(/<[^>]*>|&nbsp;|\s/g, "");

const sectionBody = (
  element: DesignElement,
  id: CompareSectionId,
  t: TFunction,
): string => {
  switch (id) {
    case "description": {
      const description = isDesignElementRisk(element)
        ? ""
        : (element.description ?? "");
      return isBlankHtml(description) ? "" : description;
    }
    case "properties":
      return paragraphs(
        propertiesOf(element, t).flatMap(({ labelKey, value }) => {
          if (!value) {
            return [];
          }
          return [
            `<strong>${escapeHtml(t(labelKey))}:</strong> ${escapeHtml(value)}`,
          ];
        }),
      );
    case "attachments":
      return isDesignElementRisk(element)
        ? ""
        : paragraphs(
            [...element.attachments]
              .sort((a, b) => a.filename.localeCompare(b.filename))
              .map((attachment) => escapeHtml(attachment.filename)),
          );
    default:
      // One row per link, so htmldiff marks a whole link as added or removed,
      // and a version bump on a link shows up as a change.
      return paragraphs(
        linkedElementsFor(element, id).map((linked) =>
          escapeHtml(
            `${linked.code} — ${linked.title}${formatVersion(
              linked.major,
              linked.minor,
            )}`,
          ),
        ),
      );
  }
};

// Markup mirrors document-editor's DocumentPresentationUtils, so the diff
// renders like the Documents compare page.
const section = (title: string, body: string): string =>
  `<h1 class="section-heading"><span>${escapeHtml(title)}</span></h1>` +
  `<div class="ck-content">${body}</div>`;

/**
 * Serializes both versions to document HTML. Built as a pair so a section
 * that is empty on one side still has its heading on both, and htmldiff shows
 * its whole body as inserted or removed.
 */
export const toCompareHtml = (
  older: DesignElement,
  viewed: DesignElement,
  t: TFunction,
): CompareHtml => {
  const sections = COMPARE_SECTIONS.map(({ id, titleKey }) => ({
    title: t(titleKey),
    older: sectionBody(older, id, t),
    viewed: sectionBody(viewed, id, t),
  })).filter((s) => s.older || s.viewed);

  return {
    older: sections.map((s) => section(s.title, s.older)).join(""),
    viewed: sections.map((s) => section(s.title, s.viewed)).join(""),
  };
};
